import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  Button
} from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const beliefPoints = [
  {
    number: '01',
    title: 'Passion for Excellence',
    description:
      'We push boundaries of innovation and craftsmanship to deliver the highest quality sports equipment and apparel.'
  },
  {
    number: '02',
    title: 'Pursuit of Performance',
    description:
      'From elite athletes to grassroots teams, we help you reach your full potential with performance-driven products.'
  },
  {
    number: '03',
    title: 'Inclusivity and Diversity',
    description:
      'Sports should be accessible to all. We celebrate diversity and the unique talents of every participant.'
  },
  {
    number: '04',
    title: 'Teamwork and Collaboration',
    description:
      'Success comes from working together. We foster cooperation and mutual support in every partnership.'
  },
  {
    number: '05',
    title: 'Continuous Improvement',
    description:
      'We listen, adapt, and evolve our products and services to stay ahead of the game.'
  },
  {
    number: '06',
    title: 'Inspiring Others',
    description:
      'Through our products, events, and initiatives we inspire active, healthy lifestyles for all ages.'
  },
  {
    number: '07',
    title: 'Giving Back',
    description:
      'We support the communities that support us through partnerships, donations, and volunteer efforts.'
  },
  {
    number: '08',
    title: 'Fun and Enjoyment',
    description:
      'We believe sports should be joyful, exciting, and unforgettable for everyone involved.'
  }
];

const ContactCTA = () => (
  <Box
    sx={{
      backgroundImage:
        'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(34,34,34,0.9) 100%)',
      color: 'white',
      py: { xs: 8, md: 10 },
      mt: 8
    }}
  >
    <Container maxWidth="lg">
      <Typography variant="h6" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
        Contacts
      </Typography>
      <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
        For any inquiries please email or call us
      </Typography>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Email sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="mailto:info@zaror.co.uk"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                info@zaror.co.uk
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:08006351020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0800 635 1020
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:01216901020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0121 690 1020
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 4, md: 5 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to discuss your kit?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
              Send us your requirements and our team will be in touch with concepts,
              pricing, and next steps.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Contact Us
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const Overview = () => {
  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
              What We Believe
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 3 }}>
              At ZAROR, we believe in the transformative power of sports. Sports inspire,
              unite, and empower individuals and communities around the world. Here's what
              we stand for:
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: 6
              }}
            >
              <img
                src="/assets/home/overview-team.png"
                alt="Nuneaton Town FC in Zaror Kit"
                style={{ width: '100%', display: 'block' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'grey.100', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {beliefPoints.map((point) => (
              <Grid item xs={12} sm={6} md={3} key={point.number}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                    {point.number}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {point.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {point.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              boxShadow: 6
            }}
          >
            <img
              src="/assets/home/overview-action.jpg"
              alt="Team playing in Zaror kit"
              style={{ width: '100%', display: 'block' }}
            />
          </Box>
        </Container>
      </Box>

      <ContactCTA />

      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Overview;
